import type {Property} from "@/src/modules/properties/types";

import {
  findMembershipInProperty,
  findResidentPropertyById,
  getDefaultResidentContext,
  getFirstAvailableResidentContext,
  getPropertyMemberships,
  resolveMembershipInMe,
} from "../lib/resident-membership";
import {useMeStore} from "../store/me";
import {queryClient} from "../lib/query-client";
import {useFiltersStore} from "../store/filters";
import {useResidentContextStore} from "../store/resident-context";
import {useScopedFiltersStore} from "../store/scoped-filters";
import type {Me} from "../types";
import type {ResidentMembership} from "../types/resident-membership";
import {useUserPropertyStore} from "../store/user-property";

class ResidentContextService {
  clearContext(): void {
    useResidentContextStore.getState().clearContext();
  }

  initFromMe(me: Me): void {
    if (!me.isResident || !me.properties.length) {
      this.clearContext();
      return;
    }

    const {selectedPropertyId, selectedHouseholdId} = useResidentContextStore.getState();
    const restored =
      selectedPropertyId != null && selectedHouseholdId != null
        ? resolveMembershipInMe(me, selectedPropertyId, selectedHouseholdId)
        : null;

    const resolved = restored ?? getDefaultResidentContext(me);
    if (!resolved) {
      this.clearContext();
      useUserPropertyStore.getState().setUserProperty(null);
      return;
    }

    this.applyContext(me, resolved.property, resolved.membership, {clearFilters: false});
  }

  getSelectedMembership(me: Me | null): ResidentMembership | null {
    if (!me?.isResident) return null;
    const {selectedPropertyId, selectedHouseholdId} = useResidentContextStore.getState();
    if (selectedPropertyId == null || selectedHouseholdId == null) return null;
    return resolveMembershipInMe(me, selectedPropertyId, selectedHouseholdId)?.membership ?? null;
  }

  getSelectedHouseholdId(me: Me | null): number | null {
    return this.getSelectedMembership(me)?.house_hold_id ?? null;
  }

  setProperty(me: Me, property: Property): void {
    const currentHouseholdId = useResidentContextStore.getState().selectedHouseholdId;
    const membershipInProperty =
      findMembershipInProperty(property, currentHouseholdId) ??
      getPropertyMemberships(property)[0] ??
      null;

    if (!membershipInProperty) return;

    this.applyContext(me, property, membershipInProperty, {clearFilters: true});
  }

  setMembership(me: Me, membership: ResidentMembership): void {
    const propertyId = useResidentContextStore.getState().selectedPropertyId;
    const property = findResidentPropertyById(me, propertyId);
    if (!property) return;

    this.applyContext(me, property, membership, {clearFilters: true});
  }

  /**
   * After `resident_context_household_forbidden`, refresh context from `/me` and
   * skip the household that was denied (avoids re-selecting the same unit).
   */
  recoverFromForbiddenHousehold(excludedHouseholdId?: number | null): boolean {
    const me = useMeStore.getState().me;
    if (!me?.isResident) {
      this.clearContext();
      useUserPropertyStore.getState().setUserProperty(null);
      return false;
    }

    const resolved = getFirstAvailableResidentContext(me, excludedHouseholdId);
    if (!resolved) {
      this.clearContext();
      useUserPropertyStore.getState().setUserProperty(null);
      return false;
    }

    this.applyContext(me, resolved.property, resolved.membership, {clearFilters: true});

    const selectedHouseholdId = resolved.membership.house_hold_id;
    if (selectedHouseholdId !== me.house_hold_id) {
      useMeStore.setState({
        me: {...me, house_hold_id: selectedHouseholdId},
      });
    }

    return true;
  }

  /** Align property + unit after form-draft resume or other property-only restores. */
  syncPropertyContext(
    me: Me,
    property: Property,
    preferredHouseholdId?: number | null,
    options?: {clearFilters?: boolean},
  ): void {
    const membership =
      findMembershipInProperty(property, preferredHouseholdId) ??
      getPropertyMemberships(property)[0] ??
      null;
    if (!membership) return;

    this.applyContext(me, property, membership, {
      clearFilters: options?.clearFilters ?? true,
    });
  }

  private applyContext(
    me: Me,
    property: Property,
    membership: ResidentMembership,
    options: {clearFilters: boolean},
  ): void {
    const freshProperty =
      findResidentPropertyById(me, property.id) ?? property;

    useResidentContextStore.getState().setSelectedContext(
      freshProperty.id,
      membership.house_hold_id,
    );

    if (options.clearFilters) {
      useFiltersStore.getState().clearFilters();
      useScopedFiltersStore.getState().clearAllScopeFilters();
      void queryClient.invalidateQueries({queryKey: ["safelistings"]});
      void queryClient.invalidateQueries({queryKey: ["citations"]});
      void queryClient.invalidateQueries({queryKey: ["permanentparkingpermits"]});
      void queryClient.invalidateQueries({queryKey: ["households"]});
      void queryClient.invalidateQueries({queryKey: ["property_amenities"]});
      void queryClient.invalidateQueries({queryKey: ["resident-home-property-amenities"]});
      void queryClient.invalidateQueries({
        queryKey: ["safelistings", "resident-home-accountability"],
      });
    }

    useUserPropertyStore.getState().setUserProperty(freshProperty);
  }
}

export const residentContextService = new ResidentContextService();
